import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card, Badge, EmptyState, LoadingState, ErrorState } from "@/components/design-system";
import { CATEGORY_LABEL, listOwnProducts, publishProduct, unpublishProduct, deleteProduct } from "@/lib/products";
import { formatApiError } from "@/lib/api";

function StatusBadge({ status }) {
  return status === "published"
    ? <Badge variant="active">Published</Badge>
    : <Badge variant="neutral">Draft</Badge>;
}

function LogoBox({ src, name }) {
  if (src) return <img src={src} alt="" className="w-14 h-14 rounded-lg object-cover border border-[#0B1F3A]/10 bg-white" />;
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  return (
    <div className="w-14 h-14 rounded-lg bg-[#E8F1FF] border border-[#0B1F3A]/10 flex items-center justify-center text-[#2D7FF9] font-[Montserrat] font-extrabold text-xl">
      {initial}
    </div>
  );
}

export default function ProductsList() {
  const [state, setState] = useState({ status: "loading", items: [], err: "" });
  const [busy, setBusy] = useState({});

  const load = async () => {
    setState((s) => ({ ...s, status: "loading" }));
    try {
      const items = await listOwnProducts();
      setState({ status: "ready", items, err: "" });
    } catch (err) {
      setState({ status: "error", items: [], err: formatApiError(err) });
    }
  };
  useEffect(() => { load(); }, []);

  const setItemBusy = (id, v) => setBusy((b) => ({ ...b, [id]: v }));

  const onPublish = async (p) => {
    setItemBusy(p.id, "publish");
    try {
      const next = p.status === "published" ? await unpublishProduct(p.id) : await publishProduct(p.id);
      setState((s) => ({ ...s, items: s.items.map((x) => x.id === next.id ? next : x) }));
    } catch (err) { alert(formatApiError(err)); }
    finally { setItemBusy(p.id, false); }
  };
  const onDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    setItemBusy(p.id, "delete");
    try {
      await deleteProduct(p.id);
      setState((s) => ({ ...s, items: s.items.filter((x) => x.id !== p.id) }));
    } catch (err) { alert(formatApiError(err)); }
    finally { setItemBusy(p.id, false); }
  };

  return (
    <div data-testid="products-list-page">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Products</p>
          <h1 className="font-[Montserrat] font-extrabold text-3xl md:text-4xl tracking-tight text-[#0B1F3A] mt-2">My products</h1>
          <p className="text-[#0B1F3A]/70 mt-2 max-w-2xl">Add the products you want to validate. Publish when you're ready to open them to the community — draft products stay private.</p>
        </div>
        <Link to="/app/products/new"><Button data-testid="new-product-btn" variant="primary">Add product</Button></Link>
      </div>

      {state.status === "loading" && <LoadingState label="Loading your products…" />}
      {state.status === "error" && <ErrorState detail={state.err} action={<Button variant="secondary" onClick={load}>Retry</Button>} />}
      {state.status === "ready" && state.items.length === 0 && (
        <EmptyState
          title="No products yet"
          description="Add your first product to unlock validation campaigns, AI insight reports, and the ambassador programme."
          action={<Link to="/app/products/new"><Button variant="primary">Add your first product</Button></Link>}
        />
      )}

      {state.status === "ready" && state.items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="products-grid">
          {state.items.map((p) => (
            <Card key={p.id} data-testid={`product-card-${p.slug}`} className="!p-6">
              <div className="flex items-start gap-4">
                <LogoBox src={p.logo_url} name={p.name} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={p.status} />
                    <span className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60">{CATEGORY_LABEL[p.category] || p.category}</span>
                  </div>
                  <h3 className="font-[Montserrat] font-bold text-xl text-[#0B1F3A] mt-2 truncate">{p.name}</h3>
                  <p className="text-sm text-[#0B1F3A]/75 mt-1 line-clamp-2">{p.tagline}</p>
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <Link to={`/app/products/${p.id}`}><Button variant="secondary" data-testid={`edit-${p.slug}`}>Edit</Button></Link>
                <Button
                  variant={p.status === "published" ? "ghost" : "primary"}
                  onClick={() => onPublish(p)}
                  disabled={busy[p.id] === "publish"}
                  data-testid={`publish-toggle-${p.slug}`}
                >
                  {busy[p.id] === "publish" ? "…" : (p.status === "published" ? "Unpublish" : "Publish")}
                </Button>
                {p.status === "published" && (
                  <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer">
                    <Button variant="ghost" data-testid={`view-public-${p.slug}`}>View public page ↗</Button>
                  </a>
                )}
                <Button variant="danger" onClick={() => onDelete(p)} disabled={busy[p.id] === "delete"} data-testid={`delete-${p.slug}`}>
                  {busy[p.id] === "delete" ? "…" : "Delete"}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
